import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import s from './ModelVariantUnits.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialModelVariantState } from '../../reducers/initialState';
import ModelVariantUnitsListTable from './ModelVariantUnitsListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import { useParams, useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ModelVariantConfigurationContentTemplate from '../ModelVariantConfiguration/ModelVariantConfigurationContentTemplate';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import Cookies from 'js-cookie';
import { getModelVariantManager } from '../../selectors/index';

const ModelVariantUnitsList = ({
  modelVariantUnitManager = { ...initialModelVariantState.modelVariantManager.selectedModelVariant.ModelVariantUnit },
  intl,
  userProfileWidgets,
  getModelVariantUnits,
  setCurrentPage,
  setFilterData,
  uploadConfigToS3AndUpdateShadow
}) => {

  let { ModelCategoryId, ModelVariantId, ModelId } = useParams();
  const history = useNavigate();
  let modelVariantManager = useSelector(state => getModelVariantManager(state));
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let filter = modelVariantUnitManager.filter || '';
  let offset = modelVariantUnitManager.offset || 0;
  let limit = modelVariantUnitManager.limit || 1000;

  const [active, setActive] = useState(false);

  const handleClose = () => {
    setActive(false);
  };

  const handleConfirm = () => {
    uploadConfigToS3AndUpdateShadow(ModelCategoryId, ModelId, ModelVariantId);
    handleClose();
  };


  const handleUpdateShadowClick = () => {
    setActive(true);
  };

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.modelvariant" defaultMessage="Model Variant" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/model-variants-list` },
    { label: <FormattedMessage id="modelVariant.units.manageModelVariantUnits" defaultMessage="Manage Model Variant Units" /> } // Last item has no href (current page)
  ];

  const updateDialogTitle = intl.formatMessage({
    id: 'modelVariant.units.confirmPublish',
    defaultMessage: 'Are you sure you want to update the settings to all the units?'
  });

  useEffect(() => {
    let item = {
      Offset: offset,
      Limit: limit,
      Filter: filter,
      ModelVariantId: ModelVariantId,
      ModelId: ModelId
    };
    getModelVariantUnits(item);
  }, [getModelVariantUnits, ModelCategoryId, ModelId, ModelVariantId, filter]);


  const componentPositionLength = modelVariantUnitManager.ModelVariantUnitsList.length;

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };
  const searchHandler = value => {
    setCurrentPage(1);
    filteredResults(value);
  };


  const filteredResults = value => {
    let filteredList = modelVariantUnitManager.ModelVariantUnitsList;

    if (value !== '') {
      filteredList = filteredList.filter(function (item) {
        return item.UnitSerialNumber.toLowerCase().includes(value.toLowerCase());
      });
    }

    setFilterData({ filteredResults: { filteredResults: filteredList } });
  };


  const modelVariantUnitsListObj = Object.values(modelVariantUnitManager.filteredResults);

  const onManageUnitsClick = () => {
    history(`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/model-variant-configuration/${ModelVariantId}/manage-unit-variants`);
  };

  let messageId = (modelVariantUnitManager && modelVariantUnitManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const isAddAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELVARIANT_UNIT_ALLOCATE');

  const modelVariantOwner = ((modelVariantManager.selectedModelVariant?.OrganisationId) == (Cookies.get(`selectedorganisationid-${envName}`) || 0));

  return (
    <div className={s.modelVariantUnit}>
      {modelVariantUnitManager.isLoading && <LoadingSpinner />}
      {modelVariantUnitManager.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={modelVariantUnitManager.showBanner}
          status={modelVariantUnitManager.isOpSuccessfull}
          successText={messageText}
        />
      )}

      <ConfirmDialog id="updateDialogTitle" title={updateDialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />

      <ModelVariantConfigurationContentTemplate selectedPage="manageModelVariantUnits" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="modelVariant.units.manageModelVariantUnits" defaultMessage="Manage Model Variant Units" />
              <span className={s.circle}>
                {componentPositionLength}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.titleRows}>

            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                // initialText={filter}
                placeHolderTextId="modelVariant.units.search"
                data-unittest="searchUnits"
              />
            </Col>
            <Col sm={6} lg={8}>
              {isAddAllowed && modelVariantOwner && <Button
                className={s.addbutton}
                style={WidgetVisibility(userProfileWidgets, 'MODELVARIANT_UNIT_ALLOCATE')}
                onClick={onManageUnitsClick}
                data-unittest="allocateUnits"
              >
                + <FormattedMessage id="modelVariant.units.allocateUnits" defaultMessage="ALLOCATE UNITS" />
              </Button>}
              {modelVariantOwner && <Button
                className={isAddAllowed && s.shadowbutton || s.addbutton}
                style={WidgetVisibility(userProfileWidgets, 'MODELVARIANTUNITSHADOWUPDATE')}
                onClick={handleUpdateShadowClick}
                data-unittest="allocateUnits"
              >
                <FormattedMessage id="modelVariant.units.publishToAllUnits" defaultMessage="PUBLISH TO ALL UNITS" />
              </Button>}
            </Col>
          </Row>

          <div>
            <Pagination
              data={modelVariantUnitsListObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={modelVariantUnitManager.currentPage}
            >
              <ModelVariantUnitsListTable
                userProfileWidgets={userProfileWidgets}
              />
            </Pagination>
          </div>

        </div>

      </ModelVariantConfigurationContentTemplate >
    </div >
  );
};

ModelVariantUnitsList.propTypes = {
  modelVariantUnitManager: PropTypes.object.isRequired,
  userProfileWidgets: PropTypes.object.isRequired,
  getModelVariantUnits: PropTypes.func.isRequired
};

export default injectIntl(ModelVariantUnitsList);
